// TemplateContentPanel — content-form for an instantiated template project
// (Plan B2 Task 4, cogo.template.v1 content-service loop).
//
// Renders NOTHING when the open project isn't an instance of a registered
// template (no `settings.cogoApplicationMetadata.instantiatedFrom`, or its
// `templateId` no longer matches a `TEMPLATE_REGISTRY` entry) — the caller
// (ChatRail.tsx) already gates the tab on the same condition, but the panel
// stays defensive so it's safe to mount unconditionally.
//
// Hard rules (task brief):
//  - manual edits AND AI-fill results both land as DIRTY values requiring
//    [적용] — nothing is ever auto-applied.
//  - [적용] calls `applySlotValues`, which is itself fail-closed
//    (validate-first, nothing saved on any failure); per-slot failures are
//    shown inline.
//  - judge is an ADVISORY badge only — it runs in the background after a
//    fill and never blocks [적용].
//  - a 503 from either /api/templates/fill or /api/templates/judge-fill
//    renders the shared "AI 서비스가 아직 구성되지 않았어요" message.

import { useMemo, useRef, useState } from 'react'
import type { Json, TypedFailure } from '@cogo/template'
import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { toast } from '@/store/toast-store'
import { applySlotValues } from './apply-slot-values'
import { bundleFilesFromProjectSource, buildSlotFormModel, type SlotFormField } from './slot-form-model'
import { readInstantiatedFrom, resolveTemplateRegistryEntry } from './project-lineage'
import { fillTemplateSlotsBatched, judgeTemplateFill, type TemplateJudgeVerdict } from './template-ai-client'
import { useStudioLocale } from '@/i18n'
import { DesignRebindSection } from './DesignRebindSection'

const SHORT_TEXT_MAX = 80

function stringifyForDiff(value: Json): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function isDirtyField(field: SlotFormField, dirty: Record<string, Json>): boolean {
  if (!(field.slotId in dirty)) return false
  return JSON.stringify(dirty[field.slotId]) !== JSON.stringify(field.currentValue)
}

function recordCount(value: Json): number {
  return Array.isArray(value) ? value.length : 0
}

export function TemplateContentPanel() {
  const { strings } = useStudioLocale()
  const copy = strings.templateContent
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const applyInFlight = useStudioStore((s) => s.templateFormApplyInFlight)
  const editorApp = useEditorStore((s) => s.app)
  const editorBlocks = useEditorStore((s) => s.blocks)
  const editorData = useEditorStore((s) => s.data)

  const lineage = readInstantiatedFrom((projectMeta?.metadata as Record<string, unknown> | undefined) ?? null)
  const entry = resolveTemplateRegistryEntry(lineage)

  const [dirty, setDirty] = useState<Record<string, Json>>({})
  const [recordListRawText, setRecordListRawText] = useState<Record<string, string>>({})
  const [recordListParseError, setRecordListParseError] = useState<Record<string, boolean>>({})
  // Task 5 (Plan B3), Item 4 — a cleared number/currency input is tracked
  // here (slotId -> '') INSTEAD of in `dirty`, so the box can render "" even
  // though `dirty` no longer has an entry for it (a controlled <input> has
  // no other way to show "empty" once its dirty value is gone — see the
  // number/currency branch below). Keyed only while a field is mid-clear;
  // typing any digit deletes the slot's entry again.
  const [numberFieldRawText, setNumberFieldRawText] = useState<Record<string, string>>({})
  const [brief, setBrief] = useState('')
  const [fillStatus, setFillStatus] = useState<'idle' | 'loading' | 'unavailable' | 'error' | 'partial' | 'allFailed'>('idle')
  const [fillError, setFillError] = useState<string | null>(null)
  // Client-side batching (full-template fills, 100+ slots) — count of slots
  // whose batch failed on the most recent fill; rendered as an honest
  // partial-success notice alongside the (still applied) successful values.
  const [fillFailedCount, setFillFailedCount] = useState(0)
  // Progress shown only while a MULTI-batch fill is in flight — a fill at or
  // under TEMPLATE_FILL_BATCH_SIZE never sets this (see the batch client's
  // onBatchStart contract), so a small template's fill looks exactly as it
  // did before batching existed.
  const [batchProgress, setBatchProgress] = useState<{ index: number; total: number } | null>(null)
  const [judgeStatus, setJudgeStatus] = useState<'idle' | 'loading' | 'done' | 'unavailable' | 'error'>('idle')
  const [judgeVerdict, setJudgeVerdict] = useState<TemplateJudgeVerdict | null>(null)
  const [judgeReasonsOpen, setJudgeReasonsOpen] = useState(false)
  const [applyStatus, setApplyStatus] = useState<'idle' | 'loading'>('idle')
  const [applyFailures, setApplyFailures] = useState<TypedFailure[]>([])
  const [persistError, setPersistError] = useState<string | null>(null)
  // Task 5 (Plan B3), Item 3 — bumped once per handleFill invocation (at the
  // very top, before the batched fetch even starts). Doubles as the run
  // token for the WHOLE fill+judge cycle: the batch loop's `shouldAbort`
  // reads it to cut an older, still-in-flight fill's remaining batches short
  // the instant a newer fill starts, the post-await check below discards a
  // stale run's outcome entirely, and the judge `.then` callback (unchanged)
  // only applies its result when the ref still equals the token it captured
  // — otherwise a NEWER fill has since kicked off its own cycle, and this
  // response is stale and must never overwrite whatever that newer call
  // already applied (or is still applying).
  const judgeSequenceRef = useRef(0)

  const fields = useMemo(() => {
    if (!entry) return []
    const { blocks, appJson } = bundleFilesFromProjectSource({
      manifest: editorApp,
      blocks: editorBlocks,
      data: editorData,
    })
    return buildSlotFormModel(entry, blocks, appJson)
  }, [entry, editorApp, editorBlocks, editorData])

  if (!entry) return null

  const groups = new Map<string, SlotFormField[]>()
  for (const field of fields) {
    const key = field.group ?? copy.general
    const list = groups.get(key)
    if (list) list.push(field)
    else groups.set(key, [field])
  }

  const dirtyCount = Object.keys(dirty).filter((slotId) => {
    const field = fields.find((f) => f.slotId === slotId)
    return field ? isDirtyField(field, dirty) : false
  }).length

  function setFieldValue(slotId: string, value: Json) {
    setDirty((prev) => ({ ...prev, [slotId]: value }))
  }

  function failuresFor(slotId: string): TypedFailure[] {
    return applyFailures.filter((f) => f.slotId === slotId)
  }

  async function handleApply() {
    if (!entry) return
    const changed: Record<string, Json> = {}
    for (const field of fields) {
      if (isDirtyField(field, dirty)) changed[field.slotId] = dirty[field.slotId]
    }
    if (Object.keys(changed).length === 0) return

    setApplyStatus('loading')
    setApplyFailures([])
    setPersistError(null)
    try {
      const result = await applySlotValues(entry, changed)
      if (result.ok) {
        setDirty((prev) => {
          const next = { ...prev }
          for (const slotId of Object.keys(changed)) delete next[slotId]
          return next
        })
        toast.success(copy.applySuccess)
      } else {
        // Dirty state is deliberately left untouched here — a `!ok` result
        // means nothing (or, on a mid-batch persistError, only a KNOWN
        // subset already reflected server-side by applySlotValues itself)
        // was actually saved, so every field the user edited must keep
        // showing as pending rather than being silently marked "applied".
        setApplyFailures(result.failures)
        if (result.persistError) setPersistError(copy.persistError)
        toast.error(copy.applyFailed)
      }
    } catch {
      // applySlotValues is expected to resolve (never reject) for ordinary
      // persistence failures — see its own persistError handling — but this
      // guards against anything unexpected reaching here anyway so a failed
      // apply is never an unhandled promise rejection the user can't see.
      setPersistError(copy.persistError)
      toast.error(copy.applyFailed)
    } finally {
      setApplyStatus('idle')
    }
  }

  async function handleFill() {
    if (!entry) return
    // Bump the run token FIRST, before anything async starts — this is what
    // lets a brand-new handleFill call invalidate an older, still-in-flight
    // one's remaining batches (via shouldAbort below) and its eventual
    // outcome (via the post-await check below), not just its judge call.
    judgeSequenceRef.current += 1
    const runToken = judgeSequenceRef.current

    setFillStatus('loading')
    setFillError(null)
    setFillFailedCount(0)
    setBatchProgress(null)
    // readOnly fields (e.g. the bundle-level record-list slot — see
    // isUnwritableRecordListAddress) can never be honestly persisted by
    // applySlotValues in v1, so they must never even be OFFERED to the fill
    // model as a fillable slot.
    const writableFields = fields.filter((field) => !field.readOnly)
    const writableSlotIds = new Set(writableFields.map((field) => field.slotId))
    const slotsPayload = writableFields.map((field) => ({
      slotId: field.slotId,
      type: field.type,
      constraint: field.constraint,
      sourceValue: field.currentValue,
    }))

    let outcome: Awaited<ReturnType<typeof fillTemplateSlotsBatched>>
    try {
      outcome = await fillTemplateSlotsBatched(entry.pkg.templateId, brief, slotsPayload, {
        onBatchStart: (index, total) => {
          if (judgeSequenceRef.current !== runToken) return
          setBatchProgress({ index, total })
        },
        shouldAbort: () => judgeSequenceRef.current !== runToken,
      })
    } catch {
      // requestTemplateFill (and therefore the batched core built on it)
      // resolves rather than rejects for ordinary failures — this guards
      // against anything unexpected reaching here anyway.
      if (judgeSequenceRef.current !== runToken) return
      setBatchProgress(null)
      setFillStatus('error')
      return
    }

    // A newer fill run started while this one's batches were still going —
    // its remaining batches were already cut short by shouldAbort above;
    // this run's outcome is stale and must be discarded entirely rather
    // than racing the newer run's own (possibly already-applied) result.
    if (judgeSequenceRef.current !== runToken) return

    setBatchProgress(null)
    if (outcome.unavailable) {
      setFillStatus('unavailable')
      return
    }
    // Belt-and-braces: even though the request never offered a readOnly
    // slot, a misbehaving upstream could still echo one back in
    // slotValues — filter the response too, so a readOnly slot can never
    // poison `dirty` (and, transitively, never reach [적용]'s fail-closed
    // batch, where it would otherwise sink the whole apply with no in-UI
    // escape).
    const writableSlotValues: Record<string, Json> = {}
    for (const [slotId, value] of Object.entries(outcome.slotValues)) {
      if (writableSlotIds.has(slotId)) writableSlotValues[slotId] = value
    }
    const merged = { ...dirty, ...writableSlotValues }
    setDirty(merged)
    // Per-batch failures never block the batches that DID succeed — the
    // successful values above are applied as dirty regardless; this is
    // purely an honest "N field(s) couldn't be filled" notice alongside them.
    // Review finding 1 — when every batch failed for a non-503 reason (so
    // `outcome.unavailable` is false — the 503-only "AI service not
    // configured" branch above already returned) AND zero writable slot
    // values came back, the 'partial' copy ("...the rest were filled") is
    // actively false: nothing was filled. That case gets its own status and
    // notice instead of being folded into 'partial'.
    setFillFailedCount(outcome.failedSlotIds.length)
    if (outcome.failedSlotIds.length === 0) {
      setFillStatus('idle')
    } else if (Object.keys(writableSlotValues).length === 0) {
      setFillStatus('allFailed')
    } else {
      setFillStatus('partial')
    }
    // A slot the AI just filled is, by definition, no longer "mid-clear" —
    // drop any stale numberFieldRawText override for it so the field shows
    // the fresh filled value instead of an empty box left over from an
    // earlier clear (see the number/currency input branch below).
    if (Object.keys(writableSlotValues).length > 0) {
      setNumberFieldRawText((prev) => {
        let changed = false
        const next = { ...prev }
        for (const slotId of Object.keys(writableSlotValues)) {
          if (slotId in next) {
            delete next[slotId]
            changed = true
          }
        }
        return changed ? next : prev
      })
    }

    // Judge is advisory + background — it must never block the form or the
    // fill result from being reviewable/applicable. Sequence-tokened (Task
    // 5, Item 3): a stale response from an OLDER fill's judge call must
    // never overwrite the verdict a newer fill's judge call already
    // produced. Reuses `runToken` (already the current token — no need to
    // bump again) so exactly one judge call fires per non-stale fill run,
    // regardless of how many batches that run's fill took internally.
    setJudgeStatus('loading')
    setJudgeVerdict(null)
    judgeTemplateFill(entry.pkg.templateId, brief, merged).then((judgeOutcome) => {
      if (judgeSequenceRef.current !== runToken) return
      if (judgeOutcome.unavailable) {
        setJudgeStatus('unavailable')
        return
      }
      if (!judgeOutcome.ok || !judgeOutcome.verdict) {
        setJudgeStatus('error')
        return
      }
      setJudgeVerdict(judgeOutcome.verdict)
      setJudgeStatus('done')
    })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="template-content-panel">
      <div className="border-b border-border bg-muted/30 p-3">
        <div className="mb-1 text-xs font-semibold text-muted-foreground">{copy.aiFill}</div>
        <textarea
          data-testid="template-fill-brief"
          className="w-full rounded border bg-background p-2 text-sm"
          rows={2}
          placeholder={copy.briefPlaceholder}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
        />
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="template-fill-button"
            className="rounded border px-3 py-1 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleFill}
            disabled={fillStatus === 'loading' || !brief.trim()}
          >
            {fillStatus === 'loading' ? copy.filling : copy.aiFill}
          </button>
          {fillStatus === 'loading' && batchProgress && batchProgress.total > 1 && (
            <span data-testid="template-fill-batch-progress" className="text-xs text-muted-foreground">
              {copy.fillBatchProgress.replace('{current}', String(batchProgress.index)).replace('{total}', String(batchProgress.total))}
            </span>
          )}
          {fillStatus === 'unavailable' && (
            <span data-testid="template-fill-unavailable" className="text-xs text-muted-foreground">
              {copy.aiUnavailable}
            </span>
          )}
          {fillStatus === 'error' && (
            <span data-testid="template-fill-error" className="text-xs text-destructive">
              {fillError ?? copy.fillFailed}
            </span>
          )}
          {fillStatus === 'partial' && fillFailedCount > 0 && (
            <span data-testid="template-fill-partial" className="text-xs text-amber-700">
              {copy.fillPartialNotice.replace('{count}', String(fillFailedCount))}
            </span>
          )}
          {fillStatus === 'allFailed' && fillFailedCount > 0 && (
            <span data-testid="template-fill-all-failed" className="text-xs text-destructive">
              {copy.fillAllFailedNotice.replace('{count}', String(fillFailedCount))}
            </span>
          )}
        </div>
        {judgeStatus !== 'idle' && (
          <div className="mt-2 text-xs" data-testid="template-judge-badge">
            {judgeStatus === 'loading' && <span className="text-muted-foreground">{copy.judgeChecking}</span>}
            {judgeStatus === 'unavailable' && <span className="text-muted-foreground">{copy.aiUnavailable}</span>}
            {judgeStatus === 'error' && <span className="text-muted-foreground">{copy.judgeFailed}</span>}
            {judgeStatus === 'done' && judgeVerdict && (
              <div>
                <span
                  data-testid="template-judge-verdict"
                  className={
                    judgeVerdict.pass
                      ? 'rounded bg-success/10 px-2 py-0.5 font-medium text-success'
                      : 'rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800'
                  }
                >
                  {judgeVerdict.pass ? copy.pass : copy.caution}
                </span>
                {judgeVerdict.reasons.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="ml-2 text-muted-foreground underline"
                      onClick={() => setJudgeReasonsOpen((open) => !open)}
                      data-testid="template-judge-reasons-toggle"
                    >
                      {copy.reasons} {judgeReasonsOpen ? copy.hide : copy.show}
                    </button>
                    {judgeReasonsOpen && (
                      <ul className="mt-1 list-disc pl-4 text-muted-foreground" data-testid="template-judge-reasons">
                        {judgeVerdict.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Design-release rebinding (Plan B3 Task 4) — the spec's SECOND
          variation axis alongside this panel's own content-slot loop above.
          Gated on the exact same `designBinding.rebinding === 'allowed'`
          condition `applyDesignRebind` itself re-checks (closed-code reuse —
          see that module's header comment) — a pinned package never even
          gets the affordance rendered. */}
      {entry.pkg.designBinding.rebinding === 'allowed' && <DesignRebindSection entry={entry} />}

      <div className="flex-1 space-y-4 p-3">
        {Array.from(groups.entries()).map(([groupLabel, groupFields]) => (
          <div key={groupLabel} data-testid={`template-content-group-${groupLabel}`}>
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{groupLabel}</div>
            <div className="space-y-3">
              {groupFields.map((field) => {
                const value = field.slotId in dirty ? dirty[field.slotId] : field.currentValue
                const dirtyFlag = isDirtyField(field, dirty)
                const fieldFailures = failuresFor(field.slotId)
                return (
                  <div key={field.slotId} data-testid={`template-content-field-${field.slotId}`}>
                    <label className="mb-1 flex items-center gap-1.5 text-xs font-medium">
                      {field.label}
                      {dirtyFlag && (
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-normal text-blue-800">{copy.changed}</span>
                      )}
                    </label>

                    {field.type === 'image' && (
                      <div className="space-y-1">
                        <input
                          type="text"
                          data-testid={`slot-input-${field.slotId}`}
                          className="w-full rounded border bg-background p-2 text-sm"
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) => setFieldValue(field.slotId, e.target.value)}
                        />
                        {typeof value === 'string' && value.length > 0 && (
                          <img
                            src={value}
                            alt={field.label}
                            className="h-20 w-20 rounded border object-cover"
                            data-testid={`slot-preview-${field.slotId}`}
                          />
                        )}
                      </div>
                    )}

                    {field.type === 'record-list' && (
                      <div className="space-y-1">
                        <div className="text-[11px] text-muted-foreground" data-testid={`slot-count-${field.slotId}`}>
                          {copy.records.replace('{count}', String(recordCount(value)))}
                        </div>
                        <textarea
                          data-testid={`slot-input-${field.slotId}`}
                          className="w-full rounded border bg-background p-2 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-50"
                          rows={6}
                          disabled={field.readOnly}
                          value={recordListRawText[field.slotId] ?? JSON.stringify(value, null, 2)}
                          onChange={(e) => {
                            // Belt-and-suspenders alongside the `disabled`
                            // attribute — a readOnly field must never reach
                            // `setFieldValue`, since it can never be honestly
                            // applied (see `isUnwritableRecordListAddress`).
                            if (field.readOnly) return
                            const text = e.target.value
                            setRecordListRawText((prev) => ({ ...prev, [field.slotId]: text }))
                            try {
                              const parsed = JSON.parse(text)
                              setRecordListParseError((prev) => ({ ...prev, [field.slotId]: false }))
                              setFieldValue(field.slotId, parsed as Json)
                            } catch {
                              setRecordListParseError((prev) => ({ ...prev, [field.slotId]: true }))
                            }
                          }}
                        />
                        {field.readOnly && (
                          <div className="text-[11px] text-muted-foreground" data-testid={`slot-readonly-note-${field.slotId}`}>
                            {copy.readOnly}
                          </div>
                        )}
                        {recordListParseError[field.slotId] && (
                          <div className="text-[11px] text-destructive" data-testid={`slot-parse-error-${field.slotId}`}>
                            {copy.invalidJson}
                          </div>
                        )}
                      </div>
                    )}

                    {field.type !== 'image' && field.type !== 'record-list' && (
                      (() => {
                        const stringValue = typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value)
                        const isNumericField = field.type === 'number' || field.type === 'currency'
                        const useTextarea =
                          field.type === 'richText' ||
                          stringValue.length > SHORT_TEXT_MAX ||
                          (field.constraint?.maxLength ?? 0) > SHORT_TEXT_MAX
                        // A cleared number field shows blank via this
                        // override even though its `dirty` entry was removed
                        // (see onChange below) — `value` alone would
                        // otherwise snap back to field.currentValue the
                        // instant dirty[slotId] is deleted, since a
                        // controlled <input> has no other way to render "".
                        const displayValue =
                          isNumericField && field.slotId in numberFieldRawText ? numberFieldRawText[field.slotId] : stringValue
                        return useTextarea ? (
                          <textarea
                            data-testid={`slot-input-${field.slotId}`}
                            className="w-full rounded border bg-background p-2 text-sm"
                            rows={3}
                            value={stringValue}
                            onChange={(e) => setFieldValue(field.slotId, e.target.value)}
                          />
                        ) : (
                          <input
                            type={isNumericField ? 'number' : 'text'}
                            data-testid={`slot-input-${field.slotId}`}
                            className="w-full rounded border bg-background p-2 text-sm"
                            value={displayValue}
                            onChange={(e) => {
                              const raw = e.target.value
                              if (isNumericField && raw === '') {
                                // Clearing a number field must NOT commit
                                // `0` — that would silently turn "I erased
                                // this" into "I set this to zero". Remove
                                // any pending dirty value instead, so the
                                // slot is excluded from the [적용] payload;
                                // numberFieldRawText keeps the box visually
                                // empty in the meantime.
                                setNumberFieldRawText((prev) => ({ ...prev, [field.slotId]: '' }))
                                setDirty((prev) => {
                                  if (!(field.slotId in prev)) return prev
                                  const next = { ...prev }
                                  delete next[field.slotId]
                                  return next
                                })
                                return
                              }
                              if (isNumericField && field.slotId in numberFieldRawText) {
                                setNumberFieldRawText((prev) => {
                                  const next = { ...prev }
                                  delete next[field.slotId]
                                  return next
                                })
                              }
                              const next: Json = isNumericField ? Number(raw) : raw
                              setFieldValue(field.slotId, next)
                            }}
                          />
                        )
                      })()
                    )}

                    {dirtyFlag && (
                      <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`slot-diff-${field.slotId}`}>
                        {stringifyForDiff(field.currentValue)} → {stringifyForDiff(value)}
                      </div>
                    )}
                    {fieldFailures.map((failure, i) => (
                      <div key={i} className="mt-1 text-[11px] text-destructive" data-testid={`slot-failure-${field.slotId}`}>
                        {failure.message}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 border-t border-border bg-card p-3">
        {persistError && (
          <div className="mb-2 text-xs text-destructive" data-testid="template-apply-persist-error">
            {persistError}
          </div>
        )}
        {applyFailures.length > 0 && (
          <div className="mb-2 text-xs text-destructive" data-testid="template-apply-error-summary">
            {copy.applyFailureSummary.replace('{count}', String(applyFailures.length))}
          </div>
        )}
        <button
          type="button"
          data-testid="template-content-apply"
          className="w-full rounded border px-3 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleApply}
          disabled={dirtyCount === 0 || applyStatus === 'loading' || applyInFlight}
        >
          {applyStatus === 'loading' || applyInFlight
            ? copy.applying
            : copy.apply.replace('{count}', String(dirtyCount))}
        </button>
      </div>
    </div>
  )
}
